import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  MessageSquare, ChevronLeft, Plus, Search, Clock, 
  Heart, MessageCircle, Eye, User
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useI18n } from '../contexts/I18nContext';

const ForumCategory = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const languageFilter = searchParams.get('language');
  
  const [posts, setPosts] = useState([]);
  const [languageBoards, setLanguageBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const categoryNames = {
    'languages': t('forum.categories.languages'),
    'platform': t('forum.categories.platform'),
    'issues': t('forum.categories.issues'),
    'misc': t('forum.categories.misc')
  };

  const categoryDescriptions = {
    'languages': t('forum.categories.languagesDesc'),
    'platform': t('forum.categories.platformDesc'),
    'issues': t('forum.categories.issuesDesc'),
    'misc': t('forum.categories.miscDesc')
  };

  useEffect(() => {
    fetchPosts();
    if (categoryId === 'languages' && !languageFilter) {
      fetchLanguageBoards();
    }
  }, [categoryId, languageFilter, page]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      let url = `/forum/posts?category=${categoryId}&page=${page}`;
      if (languageFilter) {
        url += `&language_code=${languageFilter}`;
      }
      const response = await api.get(url);
      setPosts(response.data.posts);
      setTotalPages(response.data.pages);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
      toast.error(t('forum.category.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguageBoards = async () => {
    try {
      const response = await api.get('/forum/posts/languages');
      setLanguageBoards(response.data.boards);
    } catch (err) {
      console.error('Failed to fetch language boards:', err);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/forum/search?q=${encodeURIComponent(searchQuery)}&category=${categoryId}`);
      setPosts(response.data.posts);
      setTotalPages(1);
    } catch (err) {
      toast.error(t('forum.category.searchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return t('forum.category.justNow');
    if (diff < 3600000) return t('forum.category.minutesAgo', { count: Math.floor(diff / 60000) });
    if (diff < 86400000) return t('forum.category.hoursAgo', { count: Math.floor(diff / 3600000) });
    if (diff < 604800000) return t('forum.category.daysAgo', { count: Math.floor(diff / 86400000) });
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/community')}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          {t('forum.category.backToCommunity')}
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {languageFilter ? t('forum.category.discussions', { language: languageFilter }) : categoryNames[categoryId]}
          </h1>
          <p className="text-slate-400">{categoryDescriptions[categoryId]}</p>
        </div>

        {/* Search & Create */}
        <div className="flex gap-4 mb-6">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('forum.category.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
            />
          </form>
          <button
            onClick={() => navigate(`/community/forum/${categoryId}/new`)}
            className="flex items-center gap-2 px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium transition-colors"
            data-testid="create-post-btn"
          >
            <Plus className="w-5 h-5" />
            {t('forum.category.createPost')}
          </button>
        </div>

        {/* Language Boards (for languages category) */}
        {categoryId === 'languages' && !languageFilter && languageBoards.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4">{t('forum.category.languageBoards')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {languageBoards.map((board) => (
                <button
                  key={board.language_code}
                  onClick={() => navigate(`/community/forum/languages?language=${board.language_code}`)}
                  className="p-4 rounded-lg bg-slate-800 border border-slate-700 hover:border-amber-500/50 text-left transition-colors"
                >
                  <p className="text-white font-medium truncate">{board.language_name}</p>
                  <p className="text-sm text-slate-400">{t('forum.category.boardPosts', { count: board.post_count })}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Posts List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">{t('forum.category.noPostsYet')}</p>
            <p className="text-slate-500">{t('forum.category.beFirst')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <div
                key={post.post_id}
                onClick={() => navigate(`/community/forum/post/${post.post_id}`)}
                className="p-5 rounded-xl bg-slate-800 border border-slate-700 hover:border-amber-500/50 cursor-pointer transition-all"
                data-testid={`post-${post.post_id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-full bg-slate-700">
                    <User className="w-5 h-5 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-white mb-1 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-slate-400 text-sm line-clamp-2 mb-3">
                      {post.content}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {post.author_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-4 h-4" />
                        {post.like_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-4 h-4" />
                        {post.comment_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {post.view_count || 0}
                      </span>
                    </div>
                  </div>
                  {post.language_code && (
                    <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm">
                      {post.language_name || post.language_code}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                  p === page
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ForumCategory;
